import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { cn } from '@/lib/utils';
import { Heart, Loader2 } from 'lucide-react';

/**
 * Pulsante "cuore" preferiti, riusabile sul sito pubblico.
 * - Solo clienti loggati: se non loggato reindirizza al login con `next`.
 * - Toggle ottimistico con rollback in caso di errore.
 *
 * @param {string} structureId   id della struttura/esperienza (per experience = id UUID)
 * @param {string} structureType 'experience' | 'hotel' | 'restaurant'
 * @param {boolean} isFavorite   stato iniziale (dal genitore)
 * @param {(id:string, fav:boolean)=>void} onChange callback dopo toggle riuscito
 */
export function FavoriteButton({
  structureId, structureType = 'experience', isFavorite = false,
  onChange, className = '', size = 'md',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCustomer } = useCart() || {};
  const [fav, setFav] = useState(!!isFavorite);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setFav(!!isFavorite); }, [isFavorite]);

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!structureId) return;
    if (!isCustomer) {
      const next = encodeURIComponent(location.pathname + location.search);
      toast.info('Accedi per salvare tra i preferiti');
      navigate(`/cliente/accedi?next=${next}`);
      return;
    }
    setLoading(true);
    const nextFav = !fav;
    setFav(nextFav); // ottimistico
    try {
      if (nextFav) {
        await api.post('/customer/favorites', { structure_id: structureId, structure_type: structureType });
        toast.success('Aggiunto ai preferiti');
      } else {
        await api.delete(`/customer/favorites/${structureId}`);
        toast.success('Rimosso dai preferiti');
      }
      onChange?.(structureId, nextFav);
    } catch (err) {
      setFav(!nextFav); // rollback
      toast.error(formatApiError(err?.response?.data?.detail) || 'Operazione non riuscita');
    } finally {
      setLoading(false);
    }
  };

  const dim = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  return (
    <button
      type="button"
      onClick={handle}
      disabled={loading}
      aria-label={fav ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
      aria-pressed={fav}
      data-testid={`favorite-toggle-${structureId}`}
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-background/90 shadow-sm backdrop-blur',
        'transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-background',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-95 disabled:opacity-60',
        dim, className,
      )}
    >
      {loading
        ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        : <Heart className={cn('h-[18px] w-[18px] transition-colors', fav ? 'fill-primary text-primary' : 'text-foreground/70')} />}
    </button>
  );
}

export default FavoriteButton;
